/**
 * Registers — grouped, assignable manual-entry forms (backend: internal/twin/registers.go,
 * migration 0073). A register is a NAMED, ordered set of manual-entry fields (each a
 * source='manual' tag) assigned to specific operators and filled as ONE form. Submitting
 * writes one `readings` row per value at a shared timestamp (the same app-ingress path as
 * the flat data-entry), plus a submission record grouping the fill.
 *
 * Authoring (create / fields / assignees / edit / delete) is model.write; filling a
 * register (submit) is capture.write with operator scoping (an operator may only submit a
 * register they're assigned). The flat per-entity `my-manual-entry-targets` stays for the
 * legacy path (see ./my-manual-entry).
 */

import { api } from "@/lib/api-client";
import type { Paginated } from "./types";

/** One ordered field on a register — a source='manual' tag. `metric` is tags.name. */
export interface RegisterField {
  id: string;
  tag_id: string;
  metric: string;
  label: string;
  unit: string;
  /** Drives the input type + value-vs-value_text (e.g. "string_label", "analog_scalar"). */
  data_kind: string;
  sort_order: number;
  required: boolean;
}

/** An operator a register is assigned to (roster UI). */
export interface RegisterAssignee {
  user_id: string;
  email: string;
  display_name: string;
}

/** Advisory scheduling hint for a register. */
export type RegisterCadence = "per_shift" | "per_heat" | "per_batch" | "adhoc";

/** A register. `fields`/`assignees` are populated only on the single-register GET. */
export interface Register {
  id: string;
  name: string;
  /** "" if not equipment-scoped. */
  entity_id: string;
  description: string;
  cadence: RegisterCadence;
  field_count: number;
  created_at: string;
  updated_at: string;
  fields?: RegisterField[];
  assignees?: RegisterAssignee[];
}

/** A register the caller is assigned, with its ordered fields — the form to fill. */
export interface MyRegister {
  id: string;
  name: string;
  entity_id: string;
  description: string;
  cadence: RegisterCadence;
  fields: RegisterField[];
}

/**
 * One value captured in a submission — the resolved reading for a single field.
 * Numeric kinds carry `value` (+ optional `unit`); text kinds carry `value_text`.
 */
export interface SubmissionValue {
  metric: string;
  label: string;
  unit: string;
  data_kind: string;
  value: number | null;
  value_text: string;
}

/** One register fill. `values` is the per-field detail (absent on older submissions). */
export interface Submission {
  id: string;
  submitted_by: string | null;
  submitted_by_name: string;
  submitted_at: string;
  source_image_id: string | null;
  note: string;
  value_count: number;
  values: SubmissionValue[];
}

export interface CreateRegisterBody {
  name: string;
  entity_id?: string;
  description?: string;
  cadence?: RegisterCadence;
}

export interface UpdateRegisterBody {
  name?: string;
  description?: string;
  cadence?: RegisterCadence;
}

export interface AddFieldBody {
  /** Reuse an existing manual tag (the typeahead "select existing" path). When set, name/unit/data_kind come from that tag. */
  tag_id?: string;
  /** Create — or reuse the equipment's same-named manual tag. Required unless tag_id is set. */
  name?: string;
  unit?: string;
  data_kind?: string;
  label?: string;
  sort_order?: number;
  required?: boolean;
}

/** One submitted value — send `value` for numeric kinds, `value_text` for string_label. */
export interface SubmitValue {
  metric: string;
  value?: number;
  value_text?: string;
}

export interface SubmitBody {
  values: SubmitValue[];
  note?: string;
  source_image_id?: string;
}

/** Authoring + reading registers (admin surface). */
export const registers = {
  list: () => api.get<Paginated<Register>>(`/v1/twin/registers`),
  get: (id: string) => api.get<Register>(`/v1/twin/registers/${id}`),
  create: (body: CreateRegisterBody) => api.post<Register>(`/v1/twin/registers`, body),
  update: (id: string, body: UpdateRegisterBody) =>
    api.patch<Register>(`/v1/twin/registers/${id}`, body),
  remove: (id: string) => api.delete<void>(`/v1/twin/registers/${id}`),

  addField: (id: string, body: AddFieldBody) =>
    api.post<RegisterField>(`/v1/twin/registers/${id}/fields`, body),
  removeField: (id: string, fieldId: string) =>
    api.delete<void>(`/v1/twin/registers/${id}/fields/${fieldId}`),

  addAssignee: (id: string, userId: string) =>
    api.post<RegisterAssignee>(`/v1/twin/registers/${id}/assignees`, { user_id: userId }),
  removeAssignee: (id: string, userId: string) =>
    api.delete<void>(`/v1/twin/registers/${id}/assignees/${userId}`),

  submissions: (id: string) =>
    api.get<Paginated<Submission>>(`/v1/twin/registers/${id}/submissions`),
  submit: (id: string, body: SubmitBody) =>
    api.post<Submission>(`/v1/twin/registers/${id}/submissions`, body),
};

/** The caller's assigned registers with ordered fields — drives register entry. */
export const myRegisters = {
  list: () => api.get<Paginated<MyRegister>>(`/v1/twin/my-registers`),
};
